import type { PricingFeatureText } from '@/cms/components/blocks/pricing/pricing-1/types';

export function resolveMiddleIndex(length: number) {
  if (length <= 0) {
    return 0;
  }

  return Math.floor(length / 2);
}

export function formatPriceUnit(currency: string, periodLabel: string) {
  const normalizedPeriodLabel = periodLabel.trim().replace(/\/\s+/g, '/');

  if (normalizedPeriodLabel.startsWith('/')) {
    return `${currency.toUpperCase()}${normalizedPeriodLabel}`;
  }

  return `${currency.toUpperCase()} ${normalizedPeriodLabel}`;
}

export function resolveSliderProgress(
  currentIndex: number,
  totalCount: number
) {
  if (totalCount <= 1) {
    return 0;
  }

  return (currentIndex / (totalCount - 1)) * 100;
}

export function getPricingFeatureKey(feature: PricingFeatureText) {
  return `${feature.before}${feature.highlight}${feature.after}`;
}

export function renderPricingFeatureText(
  feature: PricingFeatureText,
  highlightClassName: string
) {
  if (!feature.highlight) {
    return <span>{feature.before}</span>;
  }

  return (
    <span>
      {feature.before}
      <span className={highlightClassName}>{feature.highlight}</span>
      {feature.after}
    </span>
  );
}

export function createInlinePricingFeatureText(
  text: string,
  highlight: string
): PricingFeatureText {
  const start = text.toLocaleLowerCase().indexOf(highlight.toLocaleLowerCase());

  if (start < 0) {
    return {
      before: text,
      highlight: '',
      after: '',
    };
  }

  const end = start + highlight.length;

  return {
    before: text.slice(0, start),
    highlight: text.slice(start, end),
    after: text.slice(end),
  };
}

export function parsePricingFeatureText(text: string): PricingFeatureText {
  const match = /^(.*?)\*\*([^*]+)\*\*(.*?)$/.exec(text);
  if (!match) {
    if (text.includes('**')) {
      throw new Error(`Invalid Pricing feature highlight: ${text}`);
    }
    return {
      before: text,
      highlight: '',
      after: '',
    };
  }

  return {
    before: match[1] ?? '',
    highlight: match[2] ?? '',
    after: match[3] ?? '',
  };
}

export function prependPricingCreditFeature(params: {
  creditsHighlight: string;
  creditsLabel: string;
  features: PricingFeatureText[];
}) {
  const creditFeature = parsePricingFeatureText(params.creditsLabel);
  return [
    creditFeature.highlight
      ? creditFeature
      : createInlinePricingFeatureText(
          params.creditsLabel,
          params.creditsHighlight
        ),
    ...params.features,
  ];
}

export function formatMoneyParts(params: {
  amountCents: number;
  currency: string;
  locale: string;
}) {
  const formatter = new Intl.NumberFormat(params.locale, {
    style: 'currency',
    currency: params.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
  const parts = formatter.formatToParts(params.amountCents / 100);

  return {
    symbol: parts.find((part) => part.type === 'currency')?.value ?? '$',
    amount:
      parts
        .filter((part) =>
          ['integer', 'group', 'decimal', 'fraction', 'minusSign'].includes(
            part.type
          )
        )
        .map((part) => part.value)
        .join('') || '0',
  };
}
